import { RoleEnum, SignalingOpportunityDTO } from "src/api";
import { FiExternalLink } from "react-icons/fi";
import { Link } from 'react-router-dom';
import { translateOpportunityState } from "src/utils/translations";
import { OPPORTUNITY_STATES_DATA } from "src/utils/constants";
import { Card, Accordion } from 'react-bootstrap';
import { HiCalendar } from "react-icons/hi";
import PeriodOpportunityTable from "./PeriodOpportunityTable";
import i18next from "i18next";
import { formatDate } from "src/utils";
import { useMemo } from "react";
import { useAuth } from "src/hooks/useAuth";

type PeriodData = {
  period: string;
  items: SignalingOpportunityDTO[];
  provvigioniSegnalatore: number;
  provvigioniPassive: number;
  provvigioniSegnalatoreVinte: number;
  provvigioniDealerVinte: number;
};

const OpportunityLink = ({ name, id }) => {
  return (
    <Link to={`/opportunities/${id}`} title={name}>
      {name}
      <FiExternalLink
        className="ms-1"
        style={{ width: '1.2rem', height: '1.2rem' }}
      />
    </Link>
  )
}

const PeriodOpportunitySection = ({ period, provvigioniSegnalatoreVinte = 0, provvigioniDealerVinte = 0, items = [] }: PeriodData) => {
  const { userRole } = useAuth();

  const parseDate = (rawDate?: string) => {
    if (!rawDate) return null;
    const formatted = formatDate(rawDate, 'it-IT', false);
    return formatted === 'Invalid Date' ? rawDate : formatted;
  }

  const rows = items.map(item => ({
    opportunity: <OpportunityLink name={item.Name} id={item.Id} />,
    vehicle: `${item.Marca__c || item.Brand__c} ${item.Modello__c}`,
    status: translateOpportunityState(OPPORTUNITY_STATES_DATA[item.StageName]?.label) || item.StageName,
    provvigioniSegnalatore: `€ ${item.Provvigioni_Segnalatore__c?.toFixed(2) || '0.00'}`,
    provvigioniPassive: `€ ${item.Provvigioni_passive__c?.toFixed(2) || '0.00'}`,
    provvigioniDealer: `€ ${((item.Provvigioni_passive__c || 0) - (item.Provvigioni_Segnalatore__c || 0)).toFixed(2) || '0.00'}`,

    recordType: item.recordType,
    name: item.Name,
    createdDate: parseDate(item.CreatedDate),
    phone: item.phone,
    partitaIva: item.partitaIva,
    codiceFiscale: item.codiceFiscale,
    email: item.email,
    modello: item.Modello__c,
    marca: item.Marca__c || item.Brand__c,
    stato: translateOpportunityState(OPPORTUNITY_STATES_DATA[item.StageName]?.label) || item.StageName,
  }));

  const provvSegnalatoreVinte = `${provvigioniSegnalatoreVinte?.toFixed(2) || 0.00}`;
  const provvDealerVinte = `${provvigioniDealerVinte?.toFixed(2) || 0.00}`

  const labelProvvigioniSegnalatoreVinteByRole = useMemo(() => {
    switch (userRole) {
      case RoleEnum.MASTER:
      case RoleEnum.BACKOFFICE:
      case RoleEnum.AREA_MANAGER:
        return i18next.t('provvigioni_segnalatore');
      case RoleEnum.SEGNALATORE:
        return i18next.t('commissions')
      case RoleEnum.SALES:
      case RoleEnum.SALES_DEALER:
        return i18next.t('provvigioni_segnalatore')
      default:
        return i18next.t('provvigioni_segnalatore_euro');
    }
  }, [userRole])

  const labelProvvigioniDealerVinteByRole = useMemo(() => {
    switch (userRole) {
      case RoleEnum.MASTER:
      case RoleEnum.BACKOFFICE:
      case RoleEnum.AREA_MANAGER:
        return i18next.t('provvigioni_dealer_euro');
      case RoleEnum.SALES:
      case RoleEnum.SALES_DEALER:
        return i18next.t('provvigioni_dealer_euro')
      default:
        return i18next.t('provvigioni_dealer_euro');
    }
  }, [userRole])

  return (
    <Card>
      <Card.Header className="report-period-header">
        <div className="d-flex justify-content-between align-items-center">
          <h6>
            <HiCalendar className="text-white me-1 mb-1" size={18} />
            {period?.toUpperCase()}
          </h6>
          <div className="d-flex gap-2 align-item-center justify-content-center">
            <h6>{labelProvvigioniSegnalatoreVinteByRole}: {provvSegnalatoreVinte}</h6>

            {userRole !== RoleEnum.SEGNALATORE && (
              <>
                <h6>|</h6>
                <h6>{labelProvvigioniDealerVinteByRole}: {provvDealerVinte}</h6>
              </>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className='p-0'>
        <Accordion flush>
          <PeriodOpportunityTable rows={rows} />
        </Accordion>
      </Card.Body>
    </Card>
  )
}

export default PeriodOpportunitySection;
